import * as Sentry from '@sentry/react';
import { resolveListgenUserID } from './resolveListgenUserID';

const SENTRY_DSN =
  'https://5a97e2bcc0be239894baf4b6ae12ab85@o4511545138675712.ingest.us.sentry.io/4511545154928640';

// Distinguish localhost runs from real deployed environments,
// regardless of how the bundle was built.
const getEnvironment = () => {
  const host = (typeof window !== 'undefined' && window.location?.hostname) || '';
  if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) {
    return 'localhost';
  }
  return process.env.NODE_ENV === 'production' ? 'production' : 'development';
};

/**
 * Exact browser name/version from the user agent. Order matters:
 * Chrome-based browsers (Edge, Opera, Electron) include "Chrome/" in their UA,
 * and everything includes "Safari/", so check the most specific ones first.
 */
export const getBrowserInfo = () => {
  try {
    const ua = window.navigator.userAgent || '';
    const match = (regex) => {
      const m = ua.match(regex);
      return m ? m[1] : '';
    };

    if (ua.includes('Edg/')) {
      return { name: 'Edge', version: match(/Edg\/([\d.]+)/) };
    }
    if (ua.includes('OPR/') || ua.includes('Opera')) {
      return { name: 'Opera', version: match(/OPR\/([\d.]+)/) };
    }
    if (ua.includes('SamsungBrowser/')) {
      return { name: 'Samsung Internet', version: match(/SamsungBrowser\/([\d.]+)/) };
    }
    if (ua.includes('Electron/')) {
      return { name: 'Electron', version: match(/Electron\/([\d.]+)/) };
    }
    if (ua.includes('Firefox/')) {
      return { name: 'Firefox', version: match(/Firefox\/([\d.]+)/) };
    }
    if (ua.includes('CriOS/')) {
      return { name: 'Chrome iOS', version: match(/CriOS\/([\d.]+)/) };
    }
    if (ua.includes('FxiOS/')) {
      return { name: 'Firefox iOS', version: match(/FxiOS\/([\d.]+)/) };
    }
    if (ua.includes('Chrome/')) {
      return { name: 'Chrome', version: match(/Chrome\/([\d.]+)/) };
    }
    if (ua.includes('Safari/')) {
      return { name: 'Safari', version: match(/Version\/([\d.]+)/) };
    }
    return { name: 'Unknown', version: '' };
  } catch {
    return { name: 'Unknown', version: '' };
  }
};

/**
 * Initialize Sentry. Must be called before the React app renders.
 */
export const initSentry = () => {
  const browser = getBrowserInfo();

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: getEnvironment(),
    // Exact browser on every event, filterable as tags
    initialScope: {
      tags: {
        browserName: browser.name,
        browserVersion: browser.version || 'unknown',
      },
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // By default Sentry masks ALL text (shows ***) and blocks all media.
        // This is an internal admin tool — disable masking so replays are readable.
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Tracing
    tracesSampleRate: 1.0,
    // Trace propagation adds sentry-trace/baggage headers to matching requests.
    // If the API CORS config rejects them, remove the affected host from this list.
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/apiv4i\.listgen\.com/,
      /^https:\/\/nodai\.listgen\.com/,
    ],
    // Session Replay: 10% of normal sessions, 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Send Sentry logs
    enableLogs: true,
    // Generate a synthetic stack trace for captures without one (e.g. string rejections)
    attachStacktrace: true,
    // Default depth (3) truncates nested context like
    // contexts.log.data.storageDiagnostics to "[Object]" — keep full detail
    normalizeDepth: 6,
    // Attach the ListGen user ID from storage to EVERY event (unhandled errors,
    // ErrorBoundary, etc.). Explicit callers of trackException can still override it.
    sendDefaultPii: true,
    dataCollection: {
      httpHeaders: true,
      httpBodies: true,
      genAI: true,
    },
    beforeSend(event) {
      if (!event.user || !event.user.id) {
        const listgenUserID = resolveListgenUserID();
        event.user = {
          ...(event.user || {}),
          id: listgenUserID || 'N/A',
        };
      }
      return event;
    },
  });
};
